// Tre kvalitetstrin styret af frametid: over 20 ms i snit over 2 s går et trin ned,
// under 12 ms i 10 s går et trin op. ?quality=low|mid|high eller Indstillinger låser trinnet.

export type Tier = 0 | 1 | 2;

export interface QualitySettings {
  tier: Tier;
  resolution: number;
  dots: number;
  decor: boolean;
  rain: number;
  /** Højst så mange effektpartikler (pust, gnister, fyrværkeri) ad gangen. */
  particles: number;
  /** Glød (pixi-filters) på effektlaget: kun på højt trin. */
  glow: boolean;
}

export type QualityMode = 'auto' | 'low' | 'mid' | 'high';

const NAMES: Record<string, Tier> = { low: 0, mid: 1, high: 2 };

export class Quality {
  tier: Tier;
  pinned: boolean;
  private window: number[] = [];
  private sum = 0;
  private goodMs = 0;
  private changed: ((q: QualitySettings) => void) | null = null;

  constructor(
    param: string | null,
    private maxRes: number,
    private mobile: boolean,
  ) {
    const p = param ? NAMES[param] : undefined;
    this.pinned = p !== undefined;
    this.tier = p ?? (mobile ? 1 : 2);
  }

  onChange(fn: (q: QualitySettings) => void): void {
    this.changed = fn;
  }

  get settings(): QualitySettings {
    const t = this.tier;
    return {
      tier: t,
      resolution: Math.min(this.maxRes, [1, 1.5, 2][t] ?? 1),
      dots: (this.mobile ? [300, 900, 2000] : [800, 3000, 10000])[t] ?? 300,
      decor: t > 0,
      rain: [20, 50, 90][t] ?? 20,
      particles: [90, 260, 600][t] ?? 90,
      glow: t === 2,
    };
  }

  /** Indstillingerne vælger: auto styrer selv efter frametid, ellers låses trinnet. */
  setMode(mode: QualityMode): void {
    const p = mode === 'auto' ? undefined : NAMES[mode];
    this.pinned = p !== undefined;
    const t = p ?? (this.mobile ? 1 : 2);
    if (t !== this.tier) this.set(t);
  }

  get name(): string {
    return ['low', 'mid', 'high'][this.tier] ?? 'low';
  }

  /** Kaldes én gang pr. frame med frametiden i ms. */
  sample(ms: number): void {
    if (this.pinned) return;
    const v = Math.min(100, ms);
    this.window.push(v);
    this.sum += v;
    // Ca. 2 s ved 60 fps.
    while (this.window.length > 120) this.sum -= this.window.shift() ?? 0;
    if (this.window.length < 120) return;
    const avg = this.sum / this.window.length;
    if (avg > 20 && this.tier > 0) {
      this.set((this.tier - 1) as Tier);
    } else if (avg < 12) {
      this.goodMs += v;
      if (this.goodMs > 10_000 && this.tier < 2) this.set((this.tier + 1) as Tier);
    } else {
      this.goodMs = 0;
    }
  }

  private set(t: Tier): void {
    this.tier = t;
    this.window = [];
    this.sum = 0;
    this.goodMs = 0;
    this.changed?.(this.settings);
  }
}
